import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {MigratedPolicy, MigratedPolicyIdentity} from '../../models/migrated-policy.model';
import {MigratedPoliciesService} from '../../services/migrated-policies.service';
import {MigratedPoliciesActions} from './migrated-policies.actions';
import {SearchList} from '../../models/search-list.model';
import {FormState} from '../../models/form-state.model';
import {SearchOrder} from '../../enums/search-order.enum';
import {handleFetchListPage} from '../../helpers/search.helper';
import {catchError, tap} from 'rxjs/operators';
import {Claims} from '../../models/claims.model';
import {HttpErrorResponse} from '@angular/common/http';
import {throwError} from 'rxjs';
import {ClaimsService} from '../../services/claims.service';

interface MigratedPoliciesStateModel {
    activeMigratedPolicy: MigratedPolicy;
    list: SearchList<MigratedPolicyIdentity>;
    claims: Claims;
}

const createSearchList = () => new SearchList<MigratedPolicyIdentity>({
    filters: new FormState(),
    orderByField: 'number',
    orderDirection: SearchOrder.DESC,
});

@State<MigratedPoliciesStateModel>({
    name: MigratedPoliciesState.STATE_NAME,
    defaults: {
        activeMigratedPolicy: null,
        list: createSearchList(),
        claims: null
    }
})

@Injectable()
export class MigratedPoliciesState {
    static readonly STATE_NAME = 'migratedPolicies';

    constructor(
        private store: Store,
        private migratedPoliciesService: MigratedPoliciesService,
        private modalService: ModalService,
        private translateService: TranslateService,
        private claimsService: ClaimsService
    ) {
    }

    @Selector()
    static activeMigratedPolicy(state) {
        return state.activeMigratedPolicy;
    }

    @Selector()
    static migratedPoliciesRegistryList(state) {
        return state.list;
    }

    @Selector()
    static claims(state) {
        return state.claims;
    }

    @Action(MigratedPoliciesActions.GetMigratedPolicyById)
    getById(ctx: StateContext<MigratedPoliciesStateModel>, {id}: MigratedPoliciesActions.GetMigratedPolicyById) {
        return this.migratedPoliciesService.getMigratedPolicy(id).pipe(
            tap((policy) => {
                ctx.patchState({activeMigratedPolicy: new MigratedPolicy(policy)});
            })
        );
    }

    @Action(MigratedPoliciesActions.GetListPage)
    getListPage(ctx: StateContext<MigratedPoliciesStateModel>, action: MigratedPoliciesActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.migratedPoliciesService.getMigratedPoliciesList(props));
    }

    @Action(MigratedPoliciesActions.OpenCancelPolicyModal)
    openCancelPolicyModal(ctx: StateContext<MigratedPoliciesStateModel>, action: MigratedPoliciesActions.GetListPage) {
        const policy = ctx.getState().activeMigratedPolicy;
        this.modalService.confirm({
            nzContent: this.translateService.instant('MIGRATED_POLICY_VIEW.CANCEL_CONFIRMATION',
                {policyId: policy?.number}),
            nzOnOk: () => this.store.dispatch(new MigratedPoliciesActions.CancelPolicy(policy.id)),
        });
    }

    @Action(MigratedPoliciesActions.CancelPolicy)
    cancelPolicy(ctx: StateContext<MigratedPoliciesStateModel>, {policyId}: MigratedPoliciesActions.CancelPolicy) {
        return this.migratedPoliciesService.migratedPolicyCancellation(policyId).pipe(
            tap((policy) => {
                    ctx.patchState({activeMigratedPolicy: new MigratedPolicy(policy)});
                }
            )
        );
    }

    @Action(MigratedPoliciesActions.GetInstallmentsAndClaims)
    getInstallmentsAndClaims(ctx: StateContext<MigratedPoliciesStateModel>) {
        const policyId = ctx.getState().activeMigratedPolicy.id;
        return this.claimsService.getMigratedPolicyInstallmentsAndClaims(policyId).pipe(
            tap((claims) => {
                    ctx.patchState({claims: new Claims(claims)});
                }
            ),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    this.modalService.error({
                        nzContent: this.translateService.instant('LABELS.CLAIMS_NOT_FOUND')
                    });
                }
                return throwError(error);
            })
        );
    }

    @Action(MigratedPoliciesActions.ClearListItems)
    clearListItems(ctx: StateContext<MigratedPoliciesStateModel>) {
        const {list} = ctx.getState();
        ctx.patchState({
            list: {
                ...list,
                items: null
            }
        });
    }

    @Action(MigratedPoliciesActions.ClearList)
    clearList(ctx: StateContext<MigratedPoliciesStateModel>) {
        ctx.patchState({
            list: createSearchList()
        });
    }

    @Action(MigratedPoliciesActions.ClearPolicyData)
    clearPolicyData(ctx: StateContext<MigratedPoliciesStateModel>) {
        ctx.patchState({
            activeMigratedPolicy: null,
            claims: null
        });
    }
}
